/**
 * features/therapies/api.ts — queries para Terapias (lista + detalle).
 *
 * Hooks tipados que envuelven `useQuery` (TanStack Query) sobre el singleton
 * `supabase`. Cada `queryKey` es estable por filtros para que `F6` (admin)
 * pueda invalidar bloques especificos al editar contenido.
 *
 * Reglas de los SELECTs:
 *  - Solo columnas que la UI consume (ahorra bytes y respeta RLS).
 *  - Filtros `is_active = true` + `deleted_at is null` en publicas.
 *  - Orden por `created_at desc` (lista) y `sort asc` (categorias).
 *  - Filtro por `focus_tags` usa `contains` (op @>) — indice GIN existente.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos derivados del schema. Solo las columnas que la UI lee.
// ---------------------------------------------------------------------------

type TherapyRow = Database['public']['Tables']['therapies']['Row'];
type TherapyCategoryRow = Database['public']['Tables']['therapy_categories']['Row'];

/** Therapy tal como la consumen las pages: campos esenciales tipados. */
export interface Therapy {
  id: TherapyRow['id'];
  title: TherapyRow['title'];
  slug: TherapyRow['slug'];
  summary: TherapyRow['summary'];
  description_md: TherapyRow['description_md'];
  modality: TherapyRow['modality'];
  duration_min: TherapyRow['duration_min'];
  price_cop: TherapyRow['price_cop'];
  /** `includes` jsonb -> esperado como array de strings (vease seeds). */
  includes: string[];
  /** `benefits` jsonb -> esperado como array de strings. */
  benefits: string[];
  focus_tags: TherapyRow['focus_tags'];
  category_id: TherapyRow['category_id'];
  cover_url: TherapyRow['cover_url'];
  /** Galería de imágenes secundarias. Array vacío si no hay. */
  gallery_urls: TherapyRow['gallery_urls'];
  /** Bandera de terapia destacada (migration 0102). Una sola activa a la vez. */
  is_featured: TherapyRow['is_featured'];
}

/** Categoria de terapia con icono simbolico opcional. */
export type TherapyCategory = TherapyCategoryRow;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Columnas seleccionadas para `Therapy`. Compartido por lista + detalle + relacionadas.
const THERAPY_SELECT =
  'id, title, slug, summary, description_md, modality, duration_min, price_cop, includes, benefits, focus_tags, category_id, cover_url, gallery_urls, is_featured';

/**
 * Normaliza el campo `includes` / `benefits` desde JSON -> string[].
 * Defensivo ante seeds que devuelvan null u objetos inesperados.
 */
function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/** Mapea fila cruda de Supabase al shape `Therapy` ya saneado. */
function mapTherapy(row: Record<string, unknown>): Therapy {
  return {
    id: row.id as string,
    title: row.title as string,
    slug: row.slug as string,
    summary: (row.summary as string | null) ?? null,
    description_md: (row.description_md as string | null) ?? null,
    modality: row.modality as TherapyRow['modality'],
    duration_min: row.duration_min as number,
    price_cop: row.price_cop as number,
    includes: asStringArray(row.includes),
    benefits: asStringArray(row.benefits),
    focus_tags: (row.focus_tags as string[] | null) ?? [],
    category_id: (row.category_id as string | null) ?? null,
    cover_url: (row.cover_url as string | null) ?? null,
    gallery_urls: (row.gallery_urls as string[] | null) ?? [],
    // Defensivo: si la migracion aun no esta aplicada en el entorno actual,
    // `is_featured` viene undefined. Tratamos undefined como false.
    is_featured: Boolean(row.is_featured),
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Lista todas las terapias activas. Si `focusTag` esta definido, filtra por
 * `focus_tags` que contenga ese tag (op `@>` Postgres). Orden: created_at desc.
 */
export function useTherapies(focusTag?: string): UseQueryResult<Therapy[], Error> {
  return useQuery<Therapy[], Error>({
    // queryKey segmentada por tag para invalidaciones quirurgicas.
    queryKey: ['therapies', 'list', focusTag ?? '__all__'],
    queryFn: async (): Promise<Therapy[]> => {
      let q = supabase
        .from('therapies')
        .select(THERAPY_SELECT)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (focusTag) {
        // `contains` mapea al operador `@>` de Postgres sobre el text[].
        q = q.contains('focus_tags', [focusTag]);
      }

      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => mapTherapy(row as Record<string, unknown>));
    },
  });
}

/** Catalogo de categorias ordenado por `sort asc`. */
export function useTherapyCategories(): UseQueryResult<TherapyCategory[], Error> {
  return useQuery<TherapyCategory[], Error>({
    queryKey: ['therapy_categories'],
    queryFn: async (): Promise<TherapyCategory[]> => {
      const { data, error } = await supabase
        .from('therapy_categories')
        .select('*')
        .is('deleted_at', null)
        .order('sort', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as TherapyCategory[];
    },
  });
}

/**
 * Trae una unica terapia activa por slug. `enabled` solo cuando `slug` existe
 * (evita disparar la query con string vacio en el primer render del router).
 *
 * `data === null` => terapia no encontrada (componente debe mostrar EmptyState).
 */
export function useTherapyBySlug(slug: string | undefined): UseQueryResult<Therapy | null, Error> {
  return useQuery<Therapy | null, Error>({
    queryKey: ['therapies', 'detail', slug ?? ''],
    enabled: Boolean(slug),
    queryFn: async (): Promise<Therapy | null> => {
      const { data, error } = await supabase
        .from('therapies')
        .select(THERAPY_SELECT)
        .eq('slug', slug as string)
        .eq('is_active', true)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return mapTherapy(data as Record<string, unknown>);
    },
  });
}

/**
 * Trae la UNICA terapia marcada como `is_featured = true`. Si por error hay
 * mas de una en BD, devolvemos la mas reciente. Si no hay ninguna => null.
 *
 * Usada por la seccion "Terapia destacada" de `/terapias`.
 */
export function useFeaturedTherapy(): UseQueryResult<Therapy | null, Error> {
  return useQuery<Therapy | null, Error>({
    queryKey: ['therapies', 'featured-single'],
    queryFn: async (): Promise<Therapy | null> => {
      const { data, error } = await supabase
        .from('therapies')
        .select(THERAPY_SELECT)
        .eq('is_active', true)
        .eq('is_featured', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw new Error(error.message);
      const rows = data ?? [];
      if (rows.length === 0) return null;
      return mapTherapy(rows[0] as Record<string, unknown>);
    },
  });
}

/**
 * Trae la terapia con `focus_tag = 'orientacion'` (la sesion gratuita) para
 * que el card "Quiero orientacion" pueda preseleccionarla en el BookingWizard.
 * Si hay varias con ese tag, devuelve la primera por `created_at desc`.
 *
 * `data === null` significa que no hay terapia de orientacion activa — en ese
 * caso el card "OrientacionCard" debe deshabilitar el CTA.
 */
export function useTherapyBySlugForOrientacion(): UseQueryResult<Therapy | null, Error> {
  return useQuery<Therapy | null, Error>({
    queryKey: ['therapies', 'orientacion'],
    queryFn: async (): Promise<Therapy | null> => {
      const { data, error } = await supabase
        .from('therapies')
        .select(THERAPY_SELECT)
        .eq('is_active', true)
        .is('deleted_at', null)
        .contains('focus_tags', ['orientacion'])
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw new Error(error.message);
      const rows = data ?? [];
      if (rows.length === 0) return null;
      return mapTherapy(rows[0] as Record<string, unknown>);
    },
  });
}

/**
 * Trae N terapias de la misma categoria excluyendo `currentId`. Solo se
 * dispara cuando `categoryId` esta presente.
 */
export function useRelatedTherapies(
  currentId: string | undefined,
  categoryId: string | null | undefined,
  n = 3,
): UseQueryResult<Therapy[], Error> {
  return useQuery<Therapy[], Error>({
    queryKey: ['therapies', 'related', categoryId ?? '__none__', currentId ?? '', n],
    enabled: Boolean(categoryId) && Boolean(currentId),
    queryFn: async (): Promise<Therapy[]> => {
      const { data, error } = await supabase
        .from('therapies')
        .select(THERAPY_SELECT)
        .eq('is_active', true)
        .is('deleted_at', null)
        .eq('category_id', categoryId as string)
        .neq('id', currentId as string)
        .order('created_at', { ascending: false })
        .limit(n);
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => mapTherapy(row as Record<string, unknown>));
    },
  });
}
